<template>
  <div
    class="loading-spinner-container flex flex-col items-center justify-center p-8 min-h-[200px]"
  >
    <div class="loading-spinner mb-4" role="status" :aria-label="message">
      <div class="spinner-circle"></div>
    </div>
    <p class="loading-message text-arena-text-bright text-base text-center m-0">{{ message }}</p>
  </div>
</template>

<script setup lang="ts">
import { STRINGS } from '@/constants/strings';

interface Props {
  message?: string;
}

withDefaults(defineProps<Props>(), {
  message: STRINGS.ui.loading.loadingEllipsis,
});
</script>

<style scoped>
.loading-spinner-container {
  animation: fadeInUp 300ms ease-out;
}

.spinner-circle {
  width: 40px;
  height: 40px;
  border: 4px solid var(--color-gray-200);
  border-top: 4px solid var(--color-primary-500);
  border-radius: 50%;
  animation:
    spin 1s linear infinite,
    spinner-pulse 2s ease-in-out infinite;
  position: relative;
}

.spinner-circle::after {
  content: '';
  position: absolute;
  top: -6px;
  left: -6px;
  right: -6px;
  bottom: -6px;
  border: 2px solid transparent;
  border-top: 2px solid rgba(201, 168, 76, 0.5);
  border-radius: 50%;
  animation: spin 2s linear infinite reverse;
}

.loading-message {
  animation: textFade 2s ease-in-out infinite;
}

@keyframes spinner-pulse {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(201, 168, 76, 0.4);
  }
  50% {
    box-shadow: 0 0 0 8px rgba(201, 168, 76, 0);
  }
}

@keyframes fadeInUp {
  0% {
    opacity: 0;
    transform: translateY(20px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes textFade {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
}

/* Accessibility: Respect user's motion preferences */
@media (prefers-reduced-motion: reduce) {
  .loading-spinner-container {
    animation: none;
  }

  .spinner-circle {
    animation: none;
    border-top-color: var(--color-primary-600);
    box-shadow: none;
  }

  .spinner-circle::after {
    display: none;
  }

  .loading-message {
    animation: none;
    opacity: 1;
  }

  /* Provide static visual indicator for reduced motion users */
  .spinner-circle {
    border-width: 6px;
    border-top-color: var(--color-primary-500);
    border-right-color: rgba(201, 168, 76, 0.5);
  }
}
</style>
